import { LegalPage, LegalSection } from './LegalPage';

/**
 * Privacy Policy for the Operator platform. Generic B2B SaaS policy — review
 * with counsel and tailor the data-controller, contact, and jurisdiction
 * details before relying on this for a specific deployment.
 */
export default function PrivacyPage() {
  return (
    <LegalPage title="Privacy Policy" effectiveDate="June 8, 2026">
      <p>
        This Privacy Policy describes how the Operator platform (the "Service")
        collects, uses, and protects information when you use it. Where the
        Service is provided to you through your organization, that organization
        controls the data processed on its behalf, and this policy supplements
        any agreement between us and your organization.
      </p>

      <LegalSection heading="1. Information We Collect">
        <ul>
          <li>
            <strong>Account information</strong> — such as your name, email
            address, and organization, used to authenticate and provision
            access.
          </li>
          <li>
            <strong>Content and configuration</strong> — the agents, skills,
            prompts, documents, and other data you create or upload to the
            Service.
          </li>
          <li>
            <strong>Usage data</strong> — logs, device and browser details, and
            activity needed to operate, secure, and improve the Service.
          </li>
        </ul>
      </LegalSection>

      <LegalSection heading="2. How We Use Information">
        <p>We use the information we collect to:</p>
        <ul>
          <li>Provide, maintain, and secure the Service;</li>
          <li>Authenticate users and enforce access controls;</li>
          <li>Operate agent runs and process the inputs and outputs involved;</li>
          <li>Diagnose problems, prevent abuse, and improve the Service;</li>
          <li>Comply with legal obligations.</li>
        </ul>
      </LegalSection>

      <LegalSection heading="3. AI Processing">
        <p>
          When you run an agent, your inputs and relevant configuration are
          processed by AI models to generate outputs. We process this data to
          deliver the requested functionality. We do not use Your Content to
          train foundation models except where you or your organization has
          explicitly enabled such a feature.
        </p>
      </LegalSection>

      <LegalSection heading="4. Sharing">
        <p>
          We do not sell your personal information. We may share information with
          service providers who process data on our behalf (such as cloud
          infrastructure and AI model providers), subject to appropriate
          safeguards, and where required by law or to protect the Service.
        </p>
      </LegalSection>

      <LegalSection heading="5. Data Retention">
        <p>
          We retain information for as long as needed to provide the Service and
          to meet legal, security, and operational requirements. Your
          organization may configure retention for certain data, and you may
          request deletion through your organization's administrator.
        </p>
      </LegalSection>

      <LegalSection heading="6. Security">
        <p>
          We use technical and organizational measures designed to protect
          information, including encryption in transit, access controls, and
          tenant isolation. No method of transmission or storage is completely
          secure, so we cannot guarantee absolute security.
        </p>
      </LegalSection>

      <LegalSection heading="7. Your Rights">
        <p>
          Depending on your jurisdiction, you may have rights to access,
          correct, or delete your personal information, or to object to or
          restrict certain processing. Because your data is typically controlled
          by your organization, please direct such requests to your
          organization's administrator, who can coordinate with us.
        </p>
      </LegalSection>

      <LegalSection heading="8. Changes to This Policy">
        <p>
          We may update this Privacy Policy from time to time. When we do, we
          will revise the effective date above and, where appropriate, provide
          additional notice.
        </p>
      </LegalSection>

      <LegalSection heading="9. Contact">
        <p>
          For privacy questions, contact your organization's administrator or
          your account contact. See our <a href="/terms">Terms of Service</a> for
          the terms governing use of the Service.
        </p>
      </LegalSection>
    </LegalPage>
  );
}
